import { useState, useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Panel, PanelBody } from 'components/panel';
import { Card, CardBody } from 'components/card';
import { CardHeader, CardHeaderTitle } from 'components/card/card-header';
import FieldInput from 'components/input/FieldInput';
import FormEditHeaderButtons from 'containers/partials/buttons/FormEditHeaderButtons';
import { useSessionContext } from 'contexts/SessionContext';
import TrainingSelectTests from './TrainingSelectTests';
import messages from 'messages';
import { FormattedMessage, useIntl } from 'react-intl';
import ProgramForm from 'containers/pages/exercises/programs/ProgramForm';
import SelectedExercisesCard from 'containers/partials/cards/SelectedExercisesCard';
import { NoteTypes } from 'constants.js';
import { FieldDatePicker } from 'components/input/FieldDatePicker';
import { StoreContext } from 'index';
import { usePreviewModalContext } from 'contexts/PreviewModalContext';
import { useStore } from 'stores/Store';
import useProgram from 'hooks/queries/useProgram';
import Loader from 'components/loader/Loader';

function TrainingAdvice({ personId }) {
  const {
    authStore: { entityId }
  } = useContext(StoreContext);
  const { programId } = useParams();
  const intl = useIntl();
  const { session } = useSessionContext();

  const { fetchProgram } = useProgram();
  const program = useStore(state => state.program);

  const selectedExercises = useStore(state => state.selectedExercises);
  const setSelectedExercises = useStore(state => state.setSelectedExercises);
  const selectExercises = useStore(state => state.selectExercises);
  const resetSelectedExercises = useStore(
    state => state.resetSelectedExercises
  );

  const { setModalData } = usePreviewModalContext();
  const [edit, setEdit] = useState(true);

  useEffect(() => {
    const getProgram = async () => {
      if (programId) {
        await fetchProgram({
          variables: { id: programId },
          fetchPolicy: 'network-only'
        });
      }
    };
    if (programId) {
      getProgram();
    }
    return () => {
      // Reset selected exercises when component unmounts
      resetSelectedExercises();
    };
  }, [programId]);

  useEffect(() => {
    // Initialize selected exercises with program exercises when program changes
    if (program && program.exercises) {
      setSelectedExercises([...program.exercises]);
    }
  }, [program]);

  const onCancel = () => {
    setSelectedExercises([...program.exercises]);
    setEdit(false);
  };

  if (!program) {
    return <Loader />;
  }

  return (
    <>
      <Panel>
        <PanelBody style={{ height: '100%' }}>
          <TrainingSelectTests
            session={session}
            entityId={entityId}
            personId={personId}
            edit={edit}
          />
        </PanelBody>
      </Panel>

      <Panel>
        <ProgramForm
          entityId={entityId}
          program={program}
          onComplete={() => setEdit(false)}
          style={{ height: '100%' }}
        >
          {({ submitForm, resetForm, handleChange }) => (
            <PanelBody style={{ position: 'sticky', top: -48 }}>
              <Card secondary style={{ flex: 0 }}>
                <CardHeader secondary>
                  {/*
                      // This (.o-flex) wrapper is only needed if we are in a double-panel tab view:
                      // Tabs fullWidth
                   */}
                  <div className="o-flex o-flex--justify-space-between u-margin-bottom-none">
                    <CardHeaderTitle>
                      <FormattedMessage
                        {...messages.preventionTrainingProgramTitle}
                      />
                    </CardHeaderTitle>
                    <FormEditHeaderButtons
                      edit={edit}
                      setEdit={edit => setEdit(edit)}
                      type={'button'}
                      onSubmit={submitForm}
                      onCancel={resetForm}
                    />
                  </div>
                </CardHeader>
                <CardBody secondary>
                  <div className="o-layout">
                    <div className="o-layout__item">
                      <FieldInput
                        id="title"
                        name="title"
                        placeholder={intl.formatMessage(
                          messages.programLabelName
                        )}
                        readOnly={!edit}
                      >
                        <FormattedMessage {...messages.programLabelName} />
                      </FieldInput>
                    </div>
                    <div className="o-layout__item u-2-of-3">
                      <FieldDatePicker
                        id="startdate"
                        name="startdate"
                        icon="calendar"
                        placeholder={intl.formatMessage(
                          messages.programLabelStartDate
                        )}
                        readOnly={!edit}
                        disabled={!edit}
                        onChange={handleChange}
                        required
                      >
                        <FormattedMessage {...messages.programLabelStartDate} />
                      </FieldDatePicker>
                    </div>
                    <div className="o-layout__item u-1-of-3 o-flex--align-end o-flex">
                      <FieldInput
                        id="duration"
                        name="duration"
                        placeholder={intl.formatMessage(
                          messages.programLabelWeeks
                        )}
                        readOnly={!edit}
                      >
                        <FormattedMessage {...messages.programLabelWeeks} />
                      </FieldInput>
                    </div>
                  </div>
                </CardBody>
              </Card>
              <SelectedExercisesCard
                entityId={entityId}
                edit={edit}
                onEdit={edit => setEdit(edit)}
                onPreview={exercise =>
                  setModalData({ type: 'exercise', data: exercise })
                }
                noteType={NoteTypes.PROGRAM}
                editableNotes={false}
                onCancel={onCancel}
                selectedExercises={selectedExercises}
                onSelectedExercisesChange={exercise => {
                  selectExercises(exercise);
                }}
              />
            </PanelBody>
          )}
        </ProgramForm>
      </Panel>
    </>
  );
}

export default TrainingAdvice;
